<template>
  <div class="border rounded-lg bg-white p-4 relative">
    <div class="flex flex-wrap items-center justify-between gap-2 mb-3">
      <div class="text-xs text-gray-600">
        {{ title }} -
        <template v-if="effectiveRowLimit === 0">
          showing all {{ rows.length }}
        </template>
        <template v-else>
          showing top {{ Math.min(effectiveRowLimit, rows.length) }} of {{ rows.length }}
        </template>
      </div>
      <div class="flex flex-wrap items-center justify-end gap-2">
        <label class="text-xs text-gray-600">Rows</label>
        <select :value="effectiveRowLimit" class="border rounded px-2 py-1 text-xs" @change="setRowLimit(Number($event.target.value))">
          <option v-for="opt in normalizedRowLimitOptions" :key="`row-limit-${opt}`" :value="opt">
            {{ opt === 0 ? 'All' : `Top ${opt}` }}
          </option>
        </select>
        <button
          type="button"
          class="border rounded px-2 py-1 text-xs hover:bg-gray-50"
          @click="toggleOrientation"
        >
          Orientation: {{ effectiveOrientation === 'horizontal' ? 'Horizontal' : 'Vertical' }}
        </button>
        <template v-if="effectiveOrientation === 'vertical' && displayRows.length > 0">
          <label class="text-xs text-gray-600">Bars</label>
          <select :value="verticalPageSize" class="border rounded px-2 py-1 text-xs" @change="setVerticalPageSize(Number($event.target.value))">
            <option :value="8">8</option>
            <option :value="12">12</option>
            <option :value="16">16</option>
          </select>
          <button class="border rounded px-2 py-1 text-xs hover:bg-gray-50 disabled:opacity-50" type="button" :disabled="verticalPage <= 0" @click="prevVerticalPage">Prev</button>
          <button class="border rounded px-2 py-1 text-xs hover:bg-gray-50 disabled:opacity-50" type="button" :disabled="verticalPage >= verticalTotalPages - 1" @click="nextVerticalPage">Next</button>
        </template>
      </div>
    </div>

    <div class="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3">
      <div v-for="opt in options" :key="`legend-${opt.value}`" class="inline-flex items-center gap-1 text-[11px] text-gray-700">
        <span class="inline-block w-2.5 h-2.5 rounded-sm" :style="{ backgroundColor: optionColor(opt) }"></span>
        <span>{{ opt.label || opt.value }}</span>
      </div>
    </div>

    <div v-if="!displayRows.length" class="text-xs text-gray-500">No rows to display.</div>
    <div v-else-if="effectiveOrientation === 'horizontal'" class="space-y-3 overflow-auto pr-1" :style="{ maxHeight: `${maxHeightPx}px` }">
      <div v-for="(row, idx) in displayRows" :key="rowKey(row, idx)">
        <div class="flex justify-between text-xs mb-1">
          <span class="truncate max-w-[70%]" :title="rowLabel(row, idx)">{{ rowLabel(row, idx) }}</span>
          <span class="text-gray-500">{{ rowSummary(row) }}</span>
        </div>
        <div class="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
          <div class="h-3 flex w-full">
            <div
              v-for="opt in options"
              :key="`seg-${rowKey(row, idx)}-${opt.value}`"
              class="h-3"
              :style="segmentStyle(row, opt)"
              :title="segmentTitle(row, opt)"
              @mouseenter="showTooltip($event, segmentTitle(row, opt))"
              @mousemove="moveTooltip($event)"
              @mouseleave="hideTooltip"
            ></div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="overflow-visible pb-2">
      <div class="grid gap-3 items-end pl-1 pr-1" :style="verticalGridStyle">
        <div v-for="(row, idx) in verticalRows" :key="`vertical-${rowKey(row, idx)}`" class="min-w-0">
          <div class="h-44 flex items-end justify-center">
            <div class="w-8 h-36 rounded-t overflow-hidden bg-gray-100 flex flex-col justify-end">
              <div
                v-for="opt in optionsReversed"
                :key="`vseg-${rowKey(row, idx)}-${opt.value}`"
                :style="verticalSegmentStyle(row, opt)"
                :title="segmentTitle(row, opt)"
                @mouseenter="showTooltip($event, segmentTitle(row, opt))"
                @mousemove="moveTooltip($event)"
                @mouseleave="hideTooltip"
              ></div>
            </div>
          </div>
          <div class="text-[10px] leading-tight text-gray-700 text-center mt-1 whitespace-normal break-words min-h-[2rem]" :title="rowLabel(row, idx)">
            {{ rowLabel(row, idx) }}
          </div>
        </div>
      </div>
      <div v-if="displayRows.length > verticalPageSize" class="text-[11px] text-gray-500 text-right mt-2 pr-1">
        {{ verticalRangeLabel }}
      </div>
    </div>
    <div
      v-if="tooltip.visible"
      class="fixed z-[1200] pointer-events-none bg-black text-white text-[11px] px-2 py-1 rounded shadow"
      :style="{ left: `${tooltip.left}px`, top: `${tooltip.top}px` }"
    >
      {{ tooltip.text }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'OptionDistributionChart',
  props: {
    title: {
      type: String,
      default: 'Answer option distribution',
    },
    rows: {
      type: Array,
      default: () => [],
    },
    options: {
      type: Array,
      default: () => [],
    },
    labelFn: {
      type: Function,
      default: null,
    },
    keyFn: {
      type: Function,
      default: null,
    },
    rowLimit: {
      type: Number,
      default: null,
    },
    defaultRowLimit: {
      type: Number,
      default: 20,
    },
    rowLimitOptions: {
      type: Array,
      default: () => [20, 50, 0],
    },
    orientation: {
      type: String,
      default: null,
    },
    defaultOrientation: {
      type: String,
      default: 'horizontal',
    },
    maxHeightPx: {
      type: Number,
      default: 320,
    },
  },
  data() {
    return {
      internalRowLimit: Number(this.defaultRowLimit || 0),
      internalOrientation: this.defaultOrientation === 'vertical' ? 'vertical' : 'horizontal',
      verticalPage: 0,
      verticalPageSize: 12,
      tooltip: {
        visible: false,
        text: '',
        left: 0,
        top: 0,
      },
    }
  },
  computed: {
    normalizedRowLimitOptions() {
      const opts = Array.isArray(this.rowLimitOptions) ? this.rowLimitOptions : []
      const normalized = opts
        .map(v => Number(v))
        .filter(v => Number.isFinite(v) && v >= 0)
      const unique = Array.from(new Set(normalized))
      if (!unique.length) return [20, 50, 0]
      return unique
    },
    effectiveRowLimit() {
      if (this.rowLimit !== null && this.rowLimit !== undefined) {
        const v = Number(this.rowLimit)
        return Number.isFinite(v) && v >= 0 ? v : 0
      }
      const v = Number(this.internalRowLimit)
      return Number.isFinite(v) && v >= 0 ? v : 0
    },
    effectiveOrientation() {
      const raw = this.orientation !== null && this.orientation !== undefined
        ? this.orientation
        : this.internalOrientation
      return raw === 'vertical' ? 'vertical' : 'horizontal'
    },
    displayRows() {
      const rows = Array.isArray(this.rows) ? this.rows : []
      if (this.effectiveRowLimit === 0) return rows
      return rows.slice(0, this.effectiveRowLimit)
    },
    verticalTotalPages() {
      const size = Math.max(1, Number(this.verticalPageSize || 1))
      return Math.max(1, Math.ceil(this.displayRows.length / size))
    },
    verticalRows() {
      const size = Math.max(1, Number(this.verticalPageSize || 1))
      const page = Math.max(0, Math.min(this.verticalPage, this.verticalTotalPages - 1))
      const start = page * size
      return this.displayRows.slice(start, start + size)
    },
    verticalGridStyle() {
      const cols = Math.max(this.verticalRows.length, 1)
      return {
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
      }
    },
    verticalRangeLabel() {
      const size = Math.max(1, Number(this.verticalPageSize || 1))
      const page = Math.max(0, Math.min(this.verticalPage, this.verticalTotalPages - 1))
      const start = page * size + 1
      const end = Math.min((page + 1) * size, this.displayRows.length)
      return `${start}-${end} of ${this.displayRows.length}`
    },
    optionsReversed() {
      return [...(this.options || [])].reverse()
    },
  },
  methods: {
    setRowLimit(limit) {
      const resolved = Number.isFinite(limit) && limit >= 0 ? limit : 0
      if (this.rowLimit !== null && this.rowLimit !== undefined) {
        this.$emit('update:rowLimit', resolved)
        return
      }
      this.internalRowLimit = resolved
    },
    setOrientation(value) {
      const resolved = value === 'vertical' ? 'vertical' : 'horizontal'
      if (this.orientation !== null && this.orientation !== undefined) {
        this.$emit('update:orientation', resolved)
        return
      }
      this.internalOrientation = resolved
    },
    toggleOrientation() {
      this.setOrientation(this.effectiveOrientation === 'horizontal' ? 'vertical' : 'horizontal')
    },
    setVerticalPageSize(value) {
      const resolved = [8, 12, 16].includes(value) ? value : 12
      this.verticalPageSize = resolved
      this.verticalPage = 0
    },
    prevVerticalPage() {
      this.verticalPage = Math.max(0, this.verticalPage - 1)
    },
    nextVerticalPage() {
      this.verticalPage = Math.min(this.verticalTotalPages - 1, this.verticalPage + 1)
    },
    rowKey(row, index) {
      if (this.keyFn) return String(this.keyFn(row, index))
      return String(row?.compare_key || row?.ceremony_id || index)
    },
    rowLabel(row, index) {
      if (this.labelFn) return String(this.labelFn(row, index))
      return this.rowKey(row, index)
    },
    optionColor(opt) {
      return opt?.color || '#111827'
    },
    optionPercent(row, value) {
      const options = Array.isArray(row?.options) ? row.options : []
      const hit = options.find(opt => String(opt?.value || '') === String(value || ''))
      const pct = Number(hit?.percent_of_answered || 0)
      if (!Number.isFinite(pct)) return 0
      return Math.max(0, Math.min(100, pct))
    },
    optionCount(row, value) {
      const options = Array.isArray(row?.options) ? row.options : []
      const hit = options.find(opt => String(opt?.value || '') === String(value || ''))
      const count = Number(hit?.count || 0)
      return Number.isFinite(count) ? count : 0
    },
    segmentStyle(row, opt) {
      return {
        width: `${this.optionPercent(row, opt?.value).toFixed(2)}%`,
        backgroundColor: this.optionColor(opt),
      }
    },
    verticalSegmentStyle(row, opt) {
      return {
        height: `${this.optionPercent(row, opt?.value).toFixed(2)}%`,
        backgroundColor: this.optionColor(opt),
      }
    },
    segmentTitle(row, opt) {
      const pct = this.optionPercent(row, opt?.value).toFixed(2)
      const count = this.optionCount(row, opt?.value)
      const label = opt?.label || opt?.value || 'Option'
      return `${label}: ${pct}% (${count})`
    },
    showTooltip(event, text) {
      this.tooltip.visible = true
      this.tooltip.text = text
      this.moveTooltip(event)
    },
    moveTooltip(event) {
      const x = Number(event?.clientX || 0)
      const y = Number(event?.clientY || 0)
      this.tooltip.left = x + 12
      this.tooltip.top = y + 12
    },
    hideTooltip() {
      this.tooltip.visible = false
    },
    rowSummary(row) {
      const options = Array.isArray(row?.options) ? row.options : []
      if (!options.length) return 'No answers'
      const top = [...options]
        .sort((a, b) => (Number(b?.percent_of_answered || 0) - Number(a?.percent_of_answered || 0)))
        .slice(0, 1)
      return top.map((opt) => {
        const rawLabel = opt?.label || opt?.value || 'Option'
        const label = rawLabel.length > 20 ? `${rawLabel.slice(0, 20)}...` : rawLabel
        const pct = Number(opt?.percent_of_answered || 0).toFixed(1)
        return `${label} ${pct}%`
      }).join(' | ')
    },
  },
  watch: {
    displayRows() {
      if (this.verticalPage >= this.verticalTotalPages) {
        this.verticalPage = Math.max(0, this.verticalTotalPages - 1)
      }
    },
    effectiveOrientation() {
      this.verticalPage = 0
    },
  },
}
</script>
